import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRbacApiClient } from "../../api/apiClients";
import { PaginatedData, PaginationParams } from "../../interfaces/api";
import {
  RoleName,
  Subscription,
  UserExtendedAttrs,
  UserWorkspaceRole,
  Workspace,
} from "../../interfaces/rbac";

const useUsers = (params: PaginationParams = {}) => {
  const { page, limit, sortFields } = params;
  const apiClient = useRbacApiClient<PaginatedData<UserExtendedAttrs>>();

  return useQuery({
    queryKey: ["users", page, limit, sortFields],
    queryFn: () =>
      apiClient("GET", "/user", undefined, {
        page,
        limit,
        sort_fields: sortFields ? JSON.stringify(sortFields) : undefined,
      }),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 15,
  });
};

const useUser = (userId: string | undefined) => {
  const apiClient = useRbacApiClient<UserExtendedAttrs>();

  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => apiClient("GET", `/user/${userId}`),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 15,
    enabled: !!userId,
  });
};

const useWorkspaces = () => {
  const apiClient = useRbacApiClient<Workspace[]>();

  return useQuery({
    queryKey: ["workspace"],
    queryFn: () => apiClient("GET", "/workspace"),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 15,
  });
};

const useSubscriptions = () => {
  const apiClient = useRbacApiClient<Subscription[]>();

  return useQuery({
    queryKey: ["subscription"],
    queryFn: () => apiClient("GET", "/subscription"),
  });
};

const useCreateWorkspace = () => {
  const apiClient = useRbacApiClient();

  return useMutation({
    mutationFn: (variables: {
      name: string;
      description: string;
      subscription: string;
    }) => apiClient("POST", "/workspace", variables),
  });
};

const useUpdateWorkspace = () => {
  const apiClient = useRbacApiClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      name,
      description,
    }: {
      workspaceId: string;
      name: string;
      description: string;
    }) =>
      apiClient("PATCH", `/workspace/${workspaceId}`, { name, description }),
  });
};

const useWorkspaceUsers = (
  workspaceId: string,
  params: PaginationParams = {}
) => {
  const { page, limit, sortFields } = params;
  const apiClient = useRbacApiClient<PaginatedData<UserWorkspaceRole>>();

  return useQuery({
    queryKey: ["workspaceUsers", workspaceId, page, limit, sortFields],
    queryFn: () =>
      apiClient("GET", `/workspace/${workspaceId}/users`, undefined, {
        page,
        limit,
        sort_fields: sortFields ? JSON.stringify(sortFields) : undefined,
      }),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 15,
    enabled: !!workspaceId && workspaceId !== "",
  });
};

const useUpdateWorkspaceUser = () => {
  const apiClient = useRbacApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      userId: string;
      workspaceId: string;
      role: RoleName | null;
    }) => {
      const request = {
        workspace_id: variables.workspaceId,
        role: variables.role,
      };
      await apiClient("PATCH", `/user/${variables.userId}/workspace`, request);
    },
    onMutate: (variables) => {
      queryClient.cancelQueries({
        queryKey: ["workspaceUsers", variables.workspaceId],
      });
      const previousUsers = queryClient.getQueryData<
        PaginatedData<UserWorkspaceRole>
      >(["workspaceUsers", variables.workspaceId]);
      queryClient.setQueryData(
        ["workspaceUsers", variables.workspaceId],
        (old: PaginatedData<UserWorkspaceRole> | undefined) => {
          return old
            ? {
                ...old,
                items: old.items.map((user) =>
                  user.email === variables.userId
                    ? { ...user, workspace_role: variables.role }
                    : user
                ),
              }
            : old;
        }
      );
      return { previousUsers };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        ["workspaceUsers", variables.workspaceId],
        context?.previousUsers
      );
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["workspaceUsers", variables.workspaceId],
      });
    },
  });
};

const useSearchUsers = (query: string) => {
  const apiClient = useRbacApiClient<UserExtendedAttrs[]>();

  return useQuery({
    queryKey: ["searchUsers", query],
    queryFn: () =>
      apiClient("GET", "/user/search", undefined, {
        query,
      }),
    placeholderData: keepPreviousData,
    enabled: query.length >= 2,
  });
};

export {
  useCreateWorkspace,
  useSearchUsers,
  useSubscriptions,
  useUpdateWorkspace,
  useUpdateWorkspaceUser,
  useUser,
  useUsers,
  useWorkspaces,
  useWorkspaceUsers,
};
